import {
  PlainButton,
  type IPlainButton,
} from "@medoma/components/Button/Button";
import BinIcon from "@medoma/components/icons/BinIcon";
import {
  activityOccurrenceStatusSchema,
  type IActivityOccurrence,
} from "@medoma/models/activities";
import { useLingui } from "@lingui/react/macro";
import { useMutation } from "@tanstack/react-query";
import { removeActivityOccurrence } from "@/api/Activities";
import { useErrorMessageAlert, deducedError } from "@/Utils/ErrorUtils";
import { useDialog } from "@medoma/components/Dialog/useDialog";

export const RemoveActivityOccurrence = ({
  id: occurrenceId,
  activityId,
  status,
  text,
  size = "small",
  onSuccess,
}: Pick<IActivityOccurrence, "status" | "id" | "activityId"> & {
  text?: string;
  size?: IPlainButton["size"];
  onSuccess: () => Promise<void>;
}) => {
  const { t } = useLingui();

  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutate: removeActivityOccurrenceMutation, isPending } = useMutation({
    mutationFn: () => {
      return removeActivityOccurrence({ activityId, occurrenceId });
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Det gick inte att ta bort aktivitetstillfället`} ${deducedError(error)}`,
      );
    },
    onSuccess: async () => {
      await onSuccess();
    },
  });

  const { confirm } = useDialog();

  const STANDARD_REMOVE_TEXT = t`Ta bort`;
  const buttonText = text || STANDARD_REMOVE_TEXT;

  const isOngoing = status === activityOccurrenceStatusSchema.enum.ongoing;
  const confirmMessage = isOngoing
    ? t`Är du säker på att du vill ta bort en aktivitet i ett pågående besök?`
    : t`Är du säker på att du vill ta bort aktiviteten?`;

  return (
    <PlainButton
      size={size}
      weight="light"
      onClick={async () => {
        const removeActivityOccurrenceConfirmed = await confirm({
          title: t`Ta bort aktivitet`,
          message: confirmMessage,
        });
        if (removeActivityOccurrenceConfirmed) {
          removeActivityOccurrenceMutation();
        }
      }}
      disabled={isPending}
    >
      <BinIcon />
      {buttonText}
    </PlainButton>
  );
};
